'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

type LineageRole = 'engineer' | 'farmer' | 'returning-citizen' | 'teacher' | 'scientist' | 'native';

// ─── TAQDIR METRIC FRAMING ────────────────────────────────────────────────────
// Every MRG ward data metric framed as taqdir — decree made measurable

function TaqdirMetric({
  label,
  value,
  unit = '',
  color,
  wardNote,
  mizanNote,
  amanahCall,
}: {
  label: string;
  value: string | number;
  unit?: string;
  color: string;
  wardNote: string;
  mizanNote: string;
  amanahCall: string;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ background: `${color}06`, border: `1px solid ${color}18` }}
    >
      <div className="flex items-center justify-between p-3">
        <div>
          <p className="font-mono text-[8px] uppercase tracking-widest mb-0.5" style={{ color: `${color}70` }}>{label}</p>
          <div className="flex items-baseline gap-1">
            <span className="font-mono text-xl font-black" style={{ color }}>{value}</span>
            {unit && <span className="font-mono text-[9px]" style={{ color: `${color}60` }}>{unit}</span>}
          </div>
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          className="font-mono text-[7px] uppercase tracking-widest px-2 py-1 rounded transition-all"
          style={{
            background: expanded ? `${color}12` : 'transparent',
            border: `1px solid ${expanded ? `${color}35` : 'rgba(61,54,39,0.25)'}`,
            color: expanded ? color : 'rgba(212,197,169,0.35)',
            cursor: 'pointer',
          }}
        >
          {expanded ? '▲ Taqdir' : '▼ Taqdir'}
        </button>
      </div>
      {expanded && (
        <div className="px-3 pb-3 space-y-2" style={{ borderTop: `1px solid ${color}10` }}>
          <div className="rounded p-2 mt-2" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
            <p className="font-mono text-[7px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>◈ This is a Taqdir</p>
            <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 10, lineHeight: 1.6 }}>
              This is a measure decreed to this ward — a taqdir. The number is not neutral. It is evidence of a state that the Mizan evaluates and the Amanah calls the caretaker to address.
            </p>
          </div>
          <div className="rounded p-2" style={{ background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)' }}>
            <p className="font-mono text-[7px] uppercase tracking-widest mb-1" style={{ color: 'rgba(45,122,71,0.5)' }}>Ward Context</p>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 10, lineHeight: 1.6 }}>{wardNote}</p>
          </div>
          <div className="rounded p-2" style={{ background: 'rgba(138,138,184,0.04)', border: '1px solid rgba(138,138,184,0.12)' }}>
            <p className="font-mono text-[7px] uppercase tracking-widest mb-1" style={{ color: 'rgba(138,138,184,0.5)' }}>Mizan Evaluates</p>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 10, lineHeight: 1.6 }}>{mizanNote}</p>
          </div>
          <div className="rounded p-2" style={{ background: `${color}05`, border: `1px solid ${color}15` }}>
            <p className="font-mono text-[7px] uppercase tracking-widest mb-1" style={{ color: `${color}60` }}>Amanah Calls</p>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 10, lineHeight: 1.6 }}>{amanahCall}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AMANAH TRACKER ───────────────────────────────────────────────────────────
// Role-specific Amanah tracker with domain categories

interface AmanahItem {
  id: string;
  domain: string;
  action: string;
  status: 'active' | 'pending' | 'fulfilled' | 'neglected';
  note?: string;
  taqdirLink?: string;
}

function AmanahTracker({ role, color, items }: { role: LineageRole; color: string; items: AmanahItem[] }) {
  const [localItems, setLocalItems] = useState<AmanahItem[]>(items);
  const [newNote, setNewNote] = useState<Record<string, string>>({});

  const statusColors: Record<AmanahItem['status'], string> = {
    active: '#2d8a87',
    pending: '#D4A82A',
    fulfilled: '#2d7a47',
    neglected: '#c94a2a',
  };

  const statusLabels: Record<AmanahItem['status'], string> = {
    active: 'Tending',
    pending: 'Awaiting',
    fulfilled: 'Fulfilled',
    neglected: 'Neglected — Compost',
  };

  const cycleStatus = (id: string) => {
    const cycle: AmanahItem['status'][] = ['pending', 'active', 'fulfilled', 'neglected'];
    setLocalItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const idx = cycle.indexOf(item.status);
      return { ...item, status: cycle[(idx + 1) % cycle.length] };
    }));
  };

  const fulfilled = localItems.filter(i => i.status === 'fulfilled').length;
  const total = localItems.length;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>
          Amanah Tracker — {role.replace('-', ' ')} domains
        </p>
        <span className="font-mono text-[8px]" style={{ color: `${color}70` }}>{fulfilled}/{total} fulfilled</span>
      </div>

      {/* Progress bar */}
      <div className="rounded-full h-1 overflow-hidden" style={{ background: 'rgba(61,54,39,0.3)' }}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${total > 0 ? (fulfilled / total) * 100 : 0}%`, background: color }}
        />
      </div>

      {/* Items */}
      <div className="space-y-2">
        {localItems.map(item => (
          <div key={item.id} className="rounded-lg p-3" style={{ background: `${statusColors[item.status]}05`, border: `1px solid ${statusColors[item.status]}18` }}>
            <div className="flex items-start gap-2">
              <button
                onClick={() => cycleStatus(item.id)}
                className="w-4 h-4 rounded-full flex-shrink-0 mt-0.5 transition-all"
                style={{ background: statusColors[item.status], border: `1px solid ${statusColors[item.status]}` }}
                title="Cycle status"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-mono text-[9px] font-black uppercase" style={{ color: '#d4c5a9' }}>{item.action}</p>
                  <span className="font-mono text-[7px] px-1.5 py-0.5 rounded" style={{ background: `${statusColors[item.status]}12`, color: statusColors[item.status], border: `1px solid ${statusColors[item.status]}25` }}>
                    {statusLabels[item.status]}
                  </span>
                </div>
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, marginTop: 1 }}>{item.domain}</p>
                {item.taqdirLink && (
                  <p style={{ color: 'rgba(201,168,76,0.4)', fontSize: 9, marginTop: 2, fontStyle: 'italic' }}>
                    ◈ Taqdir: {item.taqdirLink}
                  </p>
                )}
                {item.note && (
                  <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 9, marginTop: 2 }}>{item.note}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Compost note */}
      {localItems.some(i => i.status === 'neglected') && (
        <div className="rounded p-2" style={{ background: 'rgba(212,100,50,0.04)', border: '1px solid rgba(212,100,50,0.12)' }}>
          <p style={{ color: 'rgba(212,100,50,0.6)', fontSize: 9, lineHeight: 1.6 }}>
            ◈ Neglected trusts go to compost — they enrich the soil for future growth. "To slip and grow and struggle" is the expected rhythm. Tend again when ready.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── SHARED GARMENT VIEW ─────────────────────────────────────────────────────

function GarmentView({ role, color }: { role: LineageRole; color: string }) {
  const garmentData: Record<LineageRole, {
    threads: string[];
    pattern: string;
    herb: string;
    herbArabic: string;
    herbDesc: string;
    herbPurpose: string;
    garmentName: string;
    garmentArabic: string;
    niyyahStatement: string;
  }> = {
    engineer: {
      threads: ['Systems Recognition', 'Provision Mapping', 'Infrastructure Amanah', 'Mizan Audit', 'Root Fission Applied'],
      pattern: 'Geometric lattice — the architecture of provision; each node a taqdir, each edge a Sunnat Allah',
      herb: 'Rosemary (Iklil al-Jabal)',
      herbArabic: 'إِكْلِيل الْجَبَل',
      herbDesc: 'Clarity of thought, structured memory, precision in work',
      herbPurpose: 'The engineer\'s herb — sharpens the mind that measures, orders, and builds. Al-Musawwir fashions form; rosemary sharpens the faculty that reads it.',
      garmentName: 'Reshat Al-Bina',
      garmentArabic: 'رِشَاط الْبِنَاء',
      niyyahStatement: 'I build in recognition that every measurement I take is a taqdir — prescribed by Al-Qadir before the Pen wrote it.',
    },
    farmer: {
      threads: ['Soil Observation', 'Seasonal Cycle', 'Provision Shared', 'Growth Documented', 'Harvest Returned'],
      pattern: 'Spiral growth rings — the Sunnat of cultivation; each ring a season, each gap a dormancy honored',
      herb: 'Thyme (Zaatar)',
      herbArabic: 'زَعْتَر',
      herbDesc: 'Endurance through seasons, grounding in the earth, patience of the cultivator',
      herbPurpose: 'The farmer\'s herb — grows in poor soil, endures drought, returns each season. Al-Muhyi gives life; zaatar models the patience of receiving it.',
      garmentName: 'Reshat Al-Zira\'a',
      garmentArabic: 'رِشَاط الزِّرَاعَة',
      niyyahStatement: 'I plant in recognition that the soil\'s fertility is a taqdir — measured by Al-Khaliq before the seed was formed.',
    },
    'returning-citizen': {
      threads: ['Restoration Act', 'Bridge Built', 'Reform Proposed', 'Community Trust', 'Mizan Witnessed'],
      pattern: 'Mended weave — broken threads restored, stronger at the seam; the repair is visible, honored, not hidden',
      herb: 'Black Seed (Habbatus Sauda)',
      herbArabic: 'حَبَّة السَّوْدَاء',
      herbDesc: 'Healing, restoration, and the Sunnah of remedy',
      herbPurpose: 'The restorer\'s herb — "a cure for every disease except death" (Hadith). The returning citizen carries the knowledge of what is broken. Black seed carries the knowledge of what heals.',
      garmentName: 'Reshat Al-Islah',
      garmentArabic: 'رِشَاط الْإِصْلَاح',
      niyyahStatement: 'I restore in recognition that the Mizan transgressed in my ward is a taqdir — and the Amanah calls me to address it.',
    },
    teacher: {
      threads: ['Knowledge Transmitted', 'Yaqeen Deepened', 'Curriculum Designed', 'Haqq Facilitated', 'Isnad Honored'],
      pattern: 'Flowing script — knowledge as living text woven into fabric; each thread a transmission, each knot an Isnad',
      herb: 'Sage (Mariam)',
      herbArabic: 'مَرْيَم',
      herbDesc: 'Wisdom, transmission, and the clarity that comes from deep knowing',
      herbPurpose: 'The teacher\'s herb — named for Maryam (as), whose knowledge was given directly. Sage clears the mind for transmission; the teacher\'s work is to clear the path for what was already written.',
      garmentName: 'Reshat Al-Ilm',
      garmentArabic: 'رِشَاط الْعِلْم',
      niyyahStatement: 'I teach in recognition that knowledge is a taqdir — "He taught Adam the names, all of them." My curriculum is a reading of what was already given.',
    },
    scientist: {
      threads: ['Observation Submitted', 'Verification Complete', 'Research Bridged', 'Names Extracted', 'Taqdir Read'],
      pattern: 'Fractal branching — the Taqdir of measurement made visible; each branch a fission, each node a Name recognized',
      herb: 'Fennel (Shamar)',
      herbArabic: 'شَمَر',
      herbDesc: 'Precision of observation, clarity of sight, the scientist\'s lens in plant form',
      herbPurpose: 'The scientist\'s herb — fennel\'s structure is a fission sequence in miniature. Al-Khabir is All-Aware; fennel models the precision of awareness applied to the smallest detail.',
      garmentName: 'Reshat Al-Tafakkur',
      garmentArabic: 'رِشَاط التَّفَكُّر',
      niyyahStatement: 'I observe in recognition that every measurement I record was already written by the Pen. I recognize, not discover.',
    },
    native: {
      threads: ['Land Observation', 'Ancestral Knowledge', 'Stewardship Act', 'Planning Amanah', 'Cycle Honored'],
      pattern: 'Interwoven roots — ancestral memory as living pattern; the roots are invisible but they hold everything above ground',
      herb: 'Cedar (Arz)',
      herbArabic: 'أَرْز',
      herbDesc: 'Deep rootedness, longevity, and the memory of the land itself',
      herbPurpose: 'The native\'s herb — cedar lives for centuries, its roots deeper than its height. The native caretaker carries the memory of the land\'s taqdir across generations.',
      garmentName: 'Reshat Al-Ard',
      garmentArabic: 'رِشَاط الْأَرْض',
      niyyahStatement: 'I steward in recognition that the land\'s balance is a taqdir — and I am entrusted with something that is running out.',
    },
  };

  const data = garmentData[role];

  // Role-specific thread pattern SVG paths
  const threadPatterns: Record<LineageRole, React.ReactNode> = {
    engineer: (
      <>
        {/* Geometric lattice */}
        <line x1="20" y1="40" x2="60" y2="40" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="20" y1="52" x2="60" y2="52" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="20" y1="64" x2="60" y2="64" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="20" y1="76" x2="60" y2="76" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="32" y1="28" x2="32" y2="88" stroke={color} strokeWidth="0.8" strokeOpacity="0.4" />
        <line x1="44" y1="28" x2="44" y2="88" stroke={color} strokeWidth="0.8" strokeOpacity="0.4" />
        <line x1="56" y1="28" x2="56" y2="88" stroke={color} strokeWidth="0.8" strokeOpacity="0.4" />
        <circle cx="32" cy="40" r="1.5" fill={color} fillOpacity="0.6" />
        <circle cx="44" cy="52" r="1.5" fill={color} fillOpacity="0.6" />
        <circle cx="56" cy="64" r="1.5" fill={color} fillOpacity="0.6" />
        <circle cx="32" cy="76" r="1.5" fill={color} fillOpacity="0.6" />
      </>
    ),
    farmer: (
      <>
        {/* Spiral growth rings */}
        <ellipse cx="40" cy="58" rx="16" ry="8" stroke={color} strokeWidth="0.6" fill="none" strokeOpacity="0.4" />
        <ellipse cx="40" cy="58" rx="10" ry="5" stroke={color} strokeWidth="0.6" fill="none" strokeOpacity="0.5" />
        <ellipse cx="40" cy="58" rx="5" ry="2.5" stroke={color} strokeWidth="0.8" fill="none" strokeOpacity="0.7" />
        <line x1="40" y1="28" x2="40" y2="88" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" strokeDasharray="2,3" />
        <line x1="20" y1="58" x2="60" y2="58" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" strokeDasharray="2,3" />
      </>
    ),
    'returning-citizen': (
      <>
        {/* Mended weave — broken and restored */}
        <line x1="20" y1="40" x2="38" y2="40" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="42" y1="40" x2="60" y2="40" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="38" y1="38" x2="42" y2="42" stroke={color} strokeWidth="1.2" strokeOpacity="0.8" />
        <line x1="20" y1="55" x2="60" y2="55" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="20" y1="70" x2="35" y2="70" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="39" y1="70" x2="60" y2="70" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="35" y1="68" x2="39" y2="72" stroke={color} strokeWidth="1.2" strokeOpacity="0.8" />
        <line x1="20" y1="85" x2="60" y2="85" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
        <line x1="32" y1="28" x2="32" y2="88" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
        <line x1="48" y1="28" x2="48" y2="88" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
      </>
    ),
    teacher: (
      <>
        {/* Flowing script lines */}
        <path d="M22 38 Q32 34 42 38 Q52 42 60 38" stroke={color} strokeWidth="0.8" fill="none" strokeOpacity="0.5" />
        <path d="M22 50 Q35 46 45 50 Q55 54 60 50" stroke={color} strokeWidth="0.8" fill="none" strokeOpacity="0.5" />
        <path d="M22 62 Q30 58 40 62 Q50 66 60 62" stroke={color} strokeWidth="0.8" fill="none" strokeOpacity="0.5" />
        <path d="M22 74 Q33 70 43 74 Q53 78 60 74" stroke={color} strokeWidth="0.8" fill="none" strokeOpacity="0.5" />
        <line x1="40" y1="28" x2="40" y2="88" stroke={color} strokeWidth="0.4" strokeOpacity="0.25" strokeDasharray="1,4" />
      </>
    ),
    scientist: (
      <>
        {/* Fractal branching */}
        <line x1="40" y1="28" x2="40" y2="55" stroke={color} strokeWidth="1" strokeOpacity="0.6" />
        <line x1="40" y1="45" x2="28" y2="60" stroke={color} strokeWidth="0.7" strokeOpacity="0.5" />
        <line x1="40" y1="45" x2="52" y2="60" stroke={color} strokeWidth="0.7" strokeOpacity="0.5" />
        <line x1="28" y1="60" x2="22" y2="72" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
        <line x1="28" y1="60" x2="34" y2="72" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
        <line x1="52" y1="60" x2="46" y2="72" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
        <line x1="52" y1="60" x2="58" y2="72" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" />
        <circle cx="40" cy="45" r="1.5" fill={color} fillOpacity="0.7" />
        <circle cx="28" cy="60" r="1" fill={color} fillOpacity="0.6" />
        <circle cx="52" cy="60" r="1" fill={color} fillOpacity="0.6" />
      </>
    ),
    native: (
      <>
        {/* Interwoven roots */}
        <path d="M40 28 Q35 45 30 55 Q25 65 28 80" stroke={color} strokeWidth="0.7" fill="none" strokeOpacity="0.5" />
        <path d="M40 28 Q45 45 50 55 Q55 65 52 80" stroke={color} strokeWidth="0.7" fill="none" strokeOpacity="0.5" />
        <path d="M40 35 Q38 50 36 60 Q34 70 36 85" stroke={color} strokeWidth="0.5" fill="none" strokeOpacity="0.4" />
        <path d="M40 35 Q42 50 44 60 Q46 70 44 85" stroke={color} strokeWidth="0.5" fill="none" strokeOpacity="0.4" />
        <path d="M28 60 Q34 58 40 60 Q46 62 52 60" stroke={color} strokeWidth="0.6" fill="none" strokeOpacity="0.4" />
        <path d="M30 72 Q35 70 40 72 Q45 74 50 72" stroke={color} strokeWidth="0.6" fill="none" strokeOpacity="0.4" />
      </>
    ),
  };

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${color}20` }}>
      <div className="px-5 py-4 border-b" style={{ borderColor: `${color}15`, background: `${color}06` }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: `${color}80` }}>◈ Garment View</p>
        <div className="flex items-center justify-between">
          <p className="font-mono text-sm font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>{data.garmentName}</p>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${color}60`, direction: 'rtl' }}>{data.garmentArabic}</p>
        </div>
      </div>
      <div className="p-5 space-y-4">
        {/* Visual garment */}
        <div className="rounded-xl p-5 flex flex-col items-center justify-center min-h-[140px]" style={{ background: `${color}05`, border: `1px solid ${color}15` }}>
          <div className="relative w-20 h-24 mb-3">
            <svg viewBox="0 0 80 96" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <path d="M20 8 L8 24 L20 28 L20 88 L60 88 L60 28 L72 24 L60 8 L50 16 C46 20 34 20 30 16 Z"
                stroke={color} strokeWidth="1.5" fill={`${color}08`} strokeLinejoin="round" />
              {threadPatterns[role]}
            </svg>
          </div>
          <p className="font-mono text-[9px] text-center" style={{ color: `${color}70` }}>{data.pattern}</p>
        </div>

        {/* Niyyah statement */}
        <div className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
          <p className="font-mono text-[7px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.45)' }}>Niyyah of this Garment</p>
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10, lineHeight: 1.6, fontStyle: 'italic' }}>{data.niyyahStatement}</p>
        </div>

        {/* Threads woven */}
        <div>
          <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.35)' }}>Threads Woven</p>
          <div className="flex flex-wrap gap-1.5">
            {data.threads.map(t => (
              <span key={t} className="font-mono text-[9px] px-2 py-0.5 rounded" style={{ background: `${color}10`, color: `${color}80`, border: `1px solid ${color}20` }}>{t}</span>
            ))}
          </div>
        </div>

        {/* Herb recommendation */}
        <div className="rounded-lg p-3" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)' }}>
          <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(45,122,71,0.6)' }}>◈ Herb Recommendation</p>
          <div className="flex items-center gap-2 mb-1">
            <p className="font-mono text-[10px] font-black" style={{ color: '#2d7a47' }}>{data.herb}</p>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: 'rgba(45,122,71,0.5)', direction: 'rtl' }}>{data.herbArabic}</p>
          </div>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.5, marginBottom: 4 }}>{data.herbDesc}</p>
          <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 10, lineHeight: 1.5, fontStyle: 'italic' }}>{data.herbPurpose}</p>
        </div>
      </div>
    </div>
  );
}

// ─── ENGINEER DASHBOARD ───────────────────────────────────────────────────────

function EngineerDashboard() {
  const color = '#2d8a87';
  const [activePanel, setActivePanel] = useState<'architecture' | 'provision' | 'amanah' | 'garment'>('architecture');

  const systemNodes = [
    { id: 'water', label: 'Water Systems', name: 'Al-Latif', desc: 'Flow, filtration, distribution', status: 'active' },
    { id: 'energy', label: 'Energy Grid', name: 'An-Nur', desc: 'Light, power, illumination', status: 'active' },
    { id: 'food', label: 'Food Networks', name: 'Ar-Razzaq', desc: 'Provision, distribution, access', status: 'needs-attention' },
    { id: 'transit', label: 'Transit Infrastructure', name: 'Al-Hadi', desc: 'Guidance, pathways, connection', status: 'active' },
    { id: 'housing', label: 'Housing Systems', name: 'Al-Wali', desc: 'Protection, shelter, stewardship', status: 'needs-attention' },
    { id: 'comms', label: 'Communication', name: 'Al-Khabir', desc: 'Awareness, information, transmission', status: 'active' },
  ];

  const amanahItems: AmanahItem[] = [
    { id: 'e1', domain: 'Infrastructure Amanah', action: 'Document water access gaps in ward', status: 'active', taqdirLink: 'Water access rate is a taqdir — Mizan evaluates the gap' },
    { id: 'e2', domain: 'Provision Mapping', action: 'Map food desert boundaries using MRG data', status: 'pending', taqdirLink: 'Food desert percentage is a taqdir — Ar-Razzaq\'s provision is present; the bridge is the Amanah' },
    { id: 'e3', domain: 'Systems Recognition', action: 'Apply IRL Lens to a built system — extract Names', status: 'pending' },
    { id: 'e4', domain: 'Mizan Audit', action: 'Identify one system in your ward that is out of balance', status: 'pending', taqdirLink: 'System imbalance is a taqdir — the engineer\'s Amanah is to restore Mizan' },
    { id: 'e5', domain: 'Root Fission', action: 'Perform root fission on ق-د-ر in your engineering work', status: 'pending' },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-2xl px-5 py-4" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
        <div className="flex items-center gap-3 mb-1">
          <span style={{ color, fontSize: 20 }}>⚙</span>
          <div>
            <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: `${color}70` }}>Lineage Dashboard</p>
            <h3 className="font-mono text-lg font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Engineer</h3>
          </div>
          <div className="ml-auto">
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${color}60`, direction: 'rtl' }}>مُهَنْدِس</p>
          </div>
        </div>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>Systems, provision, and the architecture of creation — seen first</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'architecture', label: 'System Architecture' },
          { id: 'provision', label: 'Provision Map' },
          { id: 'amanah', label: 'Amanah Tracker' },
          { id: 'garment', label: 'Garment View' },
        ].map(p => (
          <button key={p.id} onClick={() => setActivePanel(p.id as typeof activePanel)}
            className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
            style={{ background: activePanel === p.id ? `${color}12` : 'transparent', border: `1px solid ${activePanel === p.id ? `${color}35` : 'rgba(255,255,255,0.06)'}`, color: activePanel === p.id ? color : 'rgba(212,197,169,0.4)' }}>
            {p.label}
          </button>
        ))}
      </div>

      {activePanel === 'architecture' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>System Architecture — Names mapped to infrastructure</p>
          <div className="grid grid-cols-1 gap-2">
            {systemNodes.map(node => (
              <div key={node.id} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: `${color}05`, border: `1px solid ${node.status === 'needs-attention' ? 'rgba(212,100,50,0.2)' : `${color}15`}` }}>
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: node.status === 'needs-attention' ? '#d46432' : color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-[9px] font-black uppercase" style={{ color: '#d4c5a9' }}>{node.label}</p>
                    <span className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: `${color}10`, color }}>{node.name}</span>
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10 }}>{node.desc}</p>
                </div>
                {node.status === 'needs-attention' && (
                  <span className="font-mono text-[7px] uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: 'rgba(212,100,50,0.1)', color: '#d46432', border: '1px solid rgba(212,100,50,0.2)' }}>Needs Attention</span>
                )}
              </div>
            ))}
          </div>
          <div className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
            <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, lineHeight: 1.6, fontStyle: 'italic' }}>
              "What tools is an engineer working on, and what tools does this engineer utilize toward that purpose?" — Al-Qadir Section
            </p>
          </div>
        </div>
      )}

      {activePanel === 'provision' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.35)' }}>Provision Map — ward data as taqdir</p>
          <TaqdirMetric label="Food Desert Coverage" value="67" unit="%" color={color}
            wardNote="67% of the ward's area is classified as a food desert — more than 1 mile from a full-service grocery store."
            mizanNote="The Mizan evaluates this gap: Ar-Razzaq's provision exists; the distribution system is out of balance."
            amanahCall="The engineer's Amanah: map the gap, design the bridge, document the Taqdir for the community archive."
          />
          <TaqdirMetric label="Transit Desert" value="43" unit="%" color={color}
            wardNote="43% of residents lack access to reliable public transit within a 10-minute walk."
            mizanNote="Al-Hadi guides; the transit system is the human expression of that guidance. Its absence is a Mizan transgression."
            amanahCall="Document transit gaps as IRL Lens observations. The engineer sees infrastructure as Sunnat Allah made manifest."
          />
          <TaqdirMetric label="Vacancy Rate" value="18" unit="%" color={color}
            wardNote="18% of residential properties are vacant — a measure of displacement and disinvestment."
            mizanNote="Al-Wali protects; vacant housing is protection withheld. The Mizan evaluates the conditions that produced this measure."
            amanahCall="The engineer's Amanah: propose infrastructure solutions that address vacancy as a systems problem, not a moral failure."
          />
          <Link href="/muslim-resource-guide">
            <div className="rounded-lg p-3 mt-2 cursor-pointer transition-all" style={{ background: `${color}06`, border: `1px solid ${color}18` }}>
              <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color }}>→ Open MRG for full ward data</p>
            </div>
          </Link>
        </div>
      )}

      {activePanel === 'amanah' && <AmanahTracker role="engineer" color={color} items={amanahItems} />}
      {activePanel === 'garment' && <GarmentView role="engineer" color={color} />}
    </div>
  );
}

// ─── FARMER DASHBOARD ─────────────────────────────────────────────────────────

function FarmerDashboard() {
  const color = '#D4A82A';
  const [activePanel, setActivePanel] = useState<'soil' | 'seasonal' | 'amanah' | 'garment'>('soil');

  const soilLayers = [
    { layer: 'Canopy', name: 'Al-Muhyi', desc: 'The life-giver — what is growing above ground', status: 'active', depth: '0–2ft' },
    { layer: 'Topsoil', name: 'Ar-Razzaq', desc: 'The provision layer — nutrients, organic matter', status: 'active', depth: '2–12in' },
    { layer: 'Subsoil', name: 'Al-Khaliq', desc: 'The created structure — mineral composition', status: 'needs-attention', depth: '12–36in' },
    { layer: 'Bedrock', name: 'Al-Qayyum', desc: 'The self-sustaining foundation — always present', status: 'stable', depth: '36in+' },
  ];

  const seasons = [
    { name: 'Germination', arabic: 'إِنْبَات', desc: 'Seeds awakening — Ilm Yaqeen of the soil', active: true, name2: 'Al-Muhyi' },
    { name: 'Growth', arabic: 'نُمُوّ', desc: 'Roots deepening — Ayn Yaqeen of the cycle', active: false, name2: 'Al-Bari' },
    { name: 'Harvest', arabic: 'حَصَاد', desc: 'Fruits given — Haqq Yaqeen of provision', active: false, name2: 'Ar-Razzaq' },
    { name: 'Dormancy', arabic: 'سُكُون', desc: 'Rest and composting — Al-Mumit honored', active: false, name2: 'Al-Mumit' },
  ];

  const amanahItems: AmanahItem[] = [
    { id: 'f1', domain: 'Soil Observation', action: 'Document soil health in your growing space', status: 'active', taqdirLink: 'Soil composition is a taqdir — Al-Khaliq measured it before the seed was formed' },
    { id: 'f2', domain: 'Seasonal Cycle', action: 'Record what is germinating in your ward this season', status: 'pending', taqdirLink: 'The seasonal rhythm is a taqdir — Sunnat Allah in the cycle of growth and decay' },
    { id: 'f3', domain: 'Provision Shared', action: 'Bridge one food resource to a neighbor in need', status: 'pending' },
    { id: 'f4', domain: 'Growth Documented', action: 'Submit an IRL Lens observation of a plant in your care', status: 'pending' },
    { id: 'f5', domain: 'Harvest Returned', action: 'Document what you gave back from this season\'s harvest', status: 'pending', taqdirLink: '"And nothing of it are you retainers" — Quran 15:23. The harvest is a trust, not a possession.' },
  ];

  const statusColors: Record<string, string> = { active: '#2d7a47', 'needs-attention': '#D4A82A', stable: '#2d8a87' };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl px-5 py-4" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
        <div className="flex items-center gap-3 mb-1">
          <span style={{ color, fontSize: 20 }}>⬡</span>
          <div>
            <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: `${color}70` }}>Lineage Dashboard</p>
            <h3 className="font-mono text-lg font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Farmer</h3>
          </div>
          <div className="ml-auto">
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${color}60`, direction: 'rtl' }}>فَلَّاح</p>
          </div>
        </div>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>Soil cycles and seasonal rhythms — seen first</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'soil', label: 'Soil Layers' },
          { id: 'seasonal', label: 'Seasonal Cycle' },
          { id: 'amanah', label: 'Amanah Tracker' },
          { id: 'garment', label: 'Garment View' },
        ].map(p => (
          <button key={p.id} onClick={() => setActivePanel(p.id as typeof activePanel)}
            className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
            style={{ background: activePanel === p.id ? `${color}12` : 'transparent', border: `1px solid ${activePanel === p.id ? `${color}35` : 'rgba(255,255,255,0.06)'}`, color: activePanel === p.id ? color : 'rgba(212,197,169,0.4)' }}>
            {p.label}
          </button>
        ))}
      </div>

      {activePanel === 'soil' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Soil Layers — Names mapped to depth</p>
          {soilLayers.map(layer => (
            <div key={layer.layer} className="rounded-lg p-3" style={{ background: `${statusColors[layer.status]}05`, border: `1px solid ${statusColors[layer.status]}18` }}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <p className="font-mono text-[9px] font-black uppercase" style={{ color: '#d4c5a9' }}>{layer.layer}</p>
                  <span className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: `${color}10`, color }}>{layer.name}</span>
                </div>
                <span className="font-mono text-[7px]" style={{ color: 'rgba(212,197,169,0.3)', flexShrink: 0 }}>{layer.depth}</span>
              </div>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10 }}>{layer.desc}</p>
            </div>
          ))}
          <TaqdirMetric label="Food Desert Rate" value="49" unit="%" color={color}
            wardNote="49% poverty rate in this ward — a taqdir that the Mizan evaluates and the farmer's Amanah addresses."
            mizanNote="The soil's fertility is a taqdir. The food desert is a taqdir of a different kind — a measure of what has been withheld."
            amanahCall="The farmer's Amanah: grow what you can, share what you grow, document the gap between what is and what the Mizan calls for."
          />
        </div>
      )}

      {activePanel === 'seasonal' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Seasonal Cycle — Sunnat Allah in cultivation</p>
          <div className="grid grid-cols-2 gap-2">
            {seasons.map(s => (
              <div key={s.name} className="rounded-xl p-4" style={{ background: s.active ? `${color}10` : `${color}04`, border: `1px solid ${s.active ? `${color}35` : `${color}12`}` }}>
                <div className="flex items-center gap-2 mb-1">
                  {s.active && <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />}
                  <p className="font-mono text-[9px] font-black uppercase" style={{ color: s.active ? color : 'rgba(212,197,169,0.5)' }}>{s.name}</p>
                </div>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${color}50`, direction: 'rtl', marginBottom: 4 }}>{s.arabic}</p>
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 9, lineHeight: 1.5 }}>{s.desc}</p>
                <p className="font-mono text-[8px] mt-1" style={{ color: `${color}60` }}>{s.name2}</p>
              </div>
            ))}
          </div>
          <div className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
            <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, lineHeight: 1.6, fontStyle: 'italic' }}>
              "Which kinds of plants is the gardener planting? Where, how, why, and what Ayat, Signs is that gardener pondering while doing so?" — Al-Qadir Section
            </p>
          </div>
        </div>
      )}

      {activePanel === 'amanah' && <AmanahTracker role="farmer" color={color} items={amanahItems} />}
      {activePanel === 'garment' && <GarmentView role="farmer" color={color} />}
    </div>
  );
}

// ─── RETURNING CITIZEN DASHBOARD ─────────────────────────────────────────────

function ReturningCitizenDashboard() {
  const color = '#5a5a8a';
  const [activePanel, setActivePanel] = useState<'mizan' | 'restoration' | 'amanah' | 'garment'>('mizan');

  const mizanMetrics = [
    { label: 'Recidivism Rate', value: '43', unit: '%', wardNote: 'Without support, 43% return within 3 years — a taqdir of what the system produces when Islah is absent.', mizanNote: 'Al-Adl evaluates: this measure reflects a system that punishes without restoring. The Mizan is transgressed.', amanahCall: 'The returning citizen\'s Amanah: propose reforms that address root causes, not symptoms. Document what you know from inside.' },
    { label: 'Employment Gap', value: '31', unit: '%', wardNote: '31% unemployment among returning citizens — a taqdir of structural exclusion.', mizanNote: 'Ar-Razzaq\'s provision exists; the employment gap is a Mizan transgression in the distribution system.', amanahCall: 'Bridge employment resources. Your knowledge of the gap is itself an Amanah — use it.' },
    { label: 'Housing Instability', value: '58', unit: '%', wardNote: '58% face housing instability within the first year of release.', mizanNote: 'Al-Wali protects; housing instability is protection withheld. The Mizan evaluates the conditions that produced this.', amanahCall: 'Document housing resources in the MRG. Your experience of instability is a form of Ayn Yaqeen — you\'ve seen it.' },
  ];

  const restorationDomains = [
    { domain: 'Community Trust', status: 'building', desc: 'Rebuilding the relational fabric — Mizan in the social sphere', color: '#5a5a8a' },
    { domain: 'Reform Proposals', status: 'active', desc: 'Institutional reform from lived experience — the most powerful lens', color: '#2d8a87' },
    { domain: 'Resource Bridging', status: 'active', desc: 'Connecting returning citizens to MRG resources', color: '#2d7a47' },
    { domain: 'Islah Documentation', status: 'pending', desc: 'Recording what restoration looks like in practice', color: '#D4A82A' },
  ];

  const amanahItems: AmanahItem[] = [
    { id: 'rc1', domain: 'Mizan Witness', action: 'Document one Mizan transgression you have witnessed', status: 'active', taqdirLink: 'What you witnessed is a taqdir — a measure decreed to that ward. The Mizan evaluates it.' },
    { id: 'rc2', domain: 'Reform Proposal', action: 'Draft one reform proposition for a community institution', status: 'pending', taqdirLink: '"What reforms and resource redistribution propositions does someone formerly incarcerated have?" — Al-Qadir Section' },
    { id: 'rc3', domain: 'Bridge Built', action: 'Connect one person to a reentry resource in the MRG', status: 'pending' },
    { id: 'rc4', domain: 'Community Trust', action: 'Attend one community meeting and document what you heard', status: 'pending' },
    { id: 'rc5', domain: 'Islah Documentation', action: 'Record what restoration has looked like in your own journey', status: 'pending', taqdirLink: 'Your restoration is a taqdir — a measure of what is possible when the Amanah is fulfilled.' },
  ];

  const statusColors: Record<string, string> = { building: '#D4A82A', active: '#2d7a47', pending: '#5a5a8a' };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl px-5 py-4" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
        <div className="flex items-center gap-3 mb-1">
          <span style={{ color, fontSize: 20 }}>⌘</span>
          <div>
            <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: `${color}70` }}>Lineage Dashboard</p>
            <h3 className="font-mono text-lg font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Returning Citizen</h3>
          </div>
          <div className="ml-auto">
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${color}60`, direction: 'rtl' }}>عَائِد</p>
          </div>
        </div>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>Mizan metrics and restoration progress — seen first</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'mizan', label: 'Mizan Metrics' },
          { id: 'restoration', label: 'Restoration Domains' },
          { id: 'amanah', label: 'Amanah Tracker' },
          { id: 'garment', label: 'Garment View' },
        ].map(p => (
          <button key={p.id} onClick={() => setActivePanel(p.id as typeof activePanel)}
            className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
            style={{ background: activePanel === p.id ? `${color}12` : 'transparent', border: `1px solid ${activePanel === p.id ? `${color}35` : 'rgba(255,255,255,0.06)'}`, color: activePanel === p.id ? color : 'rgba(212,197,169,0.4)' }}>
            {p.label}
          </button>
        ))}
      </div>

      {activePanel === 'mizan' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.35)' }}>Mizan Metrics — ward data as taqdir, evaluated by the balance</p>
          {mizanMetrics.map(m => (
            <TaqdirMetric key={m.label} label={m.label} value={m.value} unit={m.unit} color={color}
              wardNote={m.wardNote} mizanNote={m.mizanNote} amanahCall={m.amanahCall}
            />
          ))}
        </div>
      )}

      {activePanel === 'restoration' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Restoration Domains — Islah in practice</p>
          {restorationDomains.map(d => (
            <div key={d.domain} className="rounded-lg p-3" style={{ background: `${d.color}06`, border: `1px solid ${d.color}18` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="font-mono text-[9px] font-black uppercase" style={{ color: '#d4c5a9' }}>{d.domain}</p>
                <span className="font-mono text-[7px] uppercase px-2 py-0.5 rounded" style={{ background: `${statusColors[d.status]}10`, color: statusColors[d.status], border: `1px solid ${statusColors[d.status]}20` }}>{d.status}</span>
              </div>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10 }}>{d.desc}</p>
            </div>
          ))}
          <div className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
            <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, lineHeight: 1.6, fontStyle: 'italic' }}>
              "What reforms and resource redistribution propositions does someone that is formerly incarcerated have for existing community institutions, and what are the core tenets of their reasoning?" — Al-Qadir Section
            </p>
          </div>
        </div>
      )}

      {activePanel === 'amanah' && <AmanahTracker role="returning-citizen" color={color} items={amanahItems} />}
      {activePanel === 'garment' && <GarmentView role="returning-citizen" color={color} />}
    </div>
  );
}

// ─── TEACHER DASHBOARD ────────────────────────────────────────────────────────

function TeacherDashboard() {
  const color = '#8a8ab8';
  const [activePanel, setActivePanel] = useState<'curriculum' | 'transmission' | 'amanah' | 'garment'>('curriculum');

  const curriculumKeys = [
    { key: 'Key 01', label: 'Ayat — Signs in Creation', coverage: 'high', desc: 'Every subject contains Ayat. The teacher\'s curriculum reveals them.' },
    { key: 'Key 02', label: 'Haal — States of Being', coverage: 'medium', desc: 'The student\'s Haal determines what they can receive. The teacher reads it.' },
    { key: 'Key 03', label: 'Taqwah — Consciousness', coverage: 'low', desc: 'The curriculum\'s ultimate purpose: consciousness of the One who taught Adam the names.' },
    { key: 'Key 07', label: 'Yaqeen — Certainty', coverage: 'high', desc: 'The teacher moves students from Ilm Yaqeen (reading) to Haqq Yaqeen (experiencing).' },
  ];

  const yaqeenLevels = [
    { level: 'Ilm Yaqeen', arabic: 'عِلْم', desc: 'Knowledge certainty — the student has read it', count: 24, color: '#8a8ab8' },
    { level: 'Ayn Yaqeen', arabic: 'عَيْن', desc: 'Perceptual certainty — the student has seen evidence', count: 12, color: '#2d8a87' },
    { level: 'Haqq Yaqeen', arabic: 'حَقّ', desc: 'Reality certainty — the student has experienced it', count: 4, color: '#2d7a47' },
  ];

  const amanahItems: AmanahItem[] = [
    { id: 't1', domain: 'Curriculum Design', action: 'Identify one Ayat embedded in your existing curriculum', status: 'active', taqdirLink: 'The knowledge you teach is a taqdir — "He taught Adam the names, all of them" (Quran 2:31)' },
    { id: 't2', domain: 'Yaqeen Deepening', action: 'Design one lesson that moves students from Ilm to Ayn Yaqeen', status: 'pending' },
    { id: 't3', domain: 'Isnad Honored', action: 'Trace the chain of transmission for one piece of knowledge you teach', status: 'pending', taqdirLink: 'Isnad is the Islamic epistemological standard — who saw it, when, from what vantage, and who verified.' },
    { id: 't4', domain: 'Haqq Facilitated', action: 'Create one assignment that requires students to leave the classroom', status: 'pending', taqdirLink: 'Haqq Yaqeen requires leaving the platform. The teacher\'s highest act is to make students want to go outside.' },
  ];

  const coverageColors: Record<string, string> = { high: '#2d7a47', medium: '#D4A82A', low: '#c94a2a' };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl px-5 py-4" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
        <div className="flex items-center gap-3 mb-1">
          <span style={{ color, fontSize: 20 }}>✦</span>
          <div>
            <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: `${color}70` }}>Lineage Dashboard</p>
            <h3 className="font-mono text-lg font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Teacher</h3>
          </div>
          <div className="ml-auto">
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${color}60`, direction: 'rtl' }}>مُعَلِّم</p>
          </div>
        </div>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>Curriculum progression and knowledge transmission — seen first</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'curriculum', label: 'Curriculum Keys' },
          { id: 'transmission', label: 'Yaqeen Levels' },
          { id: 'amanah', label: 'Amanah Tracker' },
          { id: 'garment', label: 'Garment View' },
        ].map(p => (
          <button key={p.id} onClick={() => setActivePanel(p.id as typeof activePanel)}
            className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
            style={{ background: activePanel === p.id ? `${color}12` : 'transparent', border: `1px solid ${activePanel === p.id ? `${color}35` : 'rgba(255,255,255,0.06)'}`, color: activePanel === p.id ? color : 'rgba(212,197,169,0.4)' }}>
            {p.label}
          </button>
        ))}
      </div>

      {activePanel === 'curriculum' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Curriculum Keys — Keys active in your teaching</p>
          {curriculumKeys.map(k => (
            <div key={k.key} className="rounded-lg p-3" style={{ background: `${color}05`, border: `1px solid ${color}15` }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: `${color}12`, color }}>{k.key}</span>
                <p className="font-mono text-[9px] font-black uppercase" style={{ color: '#d4c5a9' }}>{k.label}</p>
                <span className="font-mono text-[7px] uppercase ml-auto px-1.5 py-0.5 rounded" style={{ background: `${coverageColors[k.coverage]}10`, color: coverageColors[k.coverage] }}>{k.coverage}</span>
              </div>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, lineHeight: 1.5 }}>{k.desc}</p>
            </div>
          ))}
        </div>
      )}

      {activePanel === 'transmission' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Yaqeen Levels — where your students are</p>
          {yaqeenLevels.map(y => (
            <div key={y.level} className="rounded-lg p-4" style={{ background: `${y.color}06`, border: `1px solid ${y.color}18` }}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <p className="font-mono text-[9px] font-black uppercase" style={{ color: y.color }}>{y.level}</p>
                  <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${y.color}60`, direction: 'rtl' }}>{y.arabic}</p>
                </div>
                <span className="font-mono text-sm font-black" style={{ color: y.color }}>{y.count}</span>
              </div>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10 }}>{y.desc}</p>
            </div>
          ))}
          <div className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
            <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, lineHeight: 1.6, fontStyle: 'italic' }}>
              "The platform's highest purpose is to push the caretaker toward Haqq Yaqeen by making Ilm and Ayn Yaqeen so vivid that the caretaker feels compelled to go experience it directly."
            </p>
          </div>
        </div>
      )}

      {activePanel === 'amanah' && <AmanahTracker role="teacher" color={color} items={amanahItems} />}
      {activePanel === 'garment' && <GarmentView role="teacher" color={color} />}
    </div>
  );
}

// ─── SCIENTIST DASHBOARD ──────────────────────────────────────────────────────

function ScientistDashboard() {
  const color = '#2d7a47';
  const [activePanel, setActivePanel] = useState<'observations' | 'names' | 'amanah' | 'garment'>('observations');

  const observationChain = [
    { id: 'o1', encounter: 'Mycorrhizal Network', names: ['Al-Latif', 'Al-Khabir'], yaqeen: 'Ayn', status: 'verified', date: '2026-03-12' },
    { id: 'o2', encounter: 'Photosynthesis Sequence', names: ['An-Nur', 'Al-Muhyi', 'Ar-Razzaq'], yaqeen: 'Haqq', status: 'submitted', date: '2026-03-10' },
    { id: 'o3', encounter: 'Seed Germination', names: ['Al-Bari', 'Al-Khaliq'], yaqeen: 'Ilm', status: 'draft', date: '2026-03-08' },
  ];

  const amanahItems: AmanahItem[] = [
    { id: 's1', domain: 'Observation Submitted', action: 'Submit one IRL Lens observation to the community archive', status: 'active', taqdirLink: 'Your observation adds to the communal tafsir of the Qadr — you recognized what the Pen wrote.' },
    { id: 's2', domain: 'Verification Complete', action: 'Verify one community observation using independent sources', status: 'pending', taqdirLink: 'Isnad for ecological recognition — the same epistemological discipline that preserves Hadith literature.' },
    { id: 's3', domain: 'Research Bridged', action: 'Connect one scientific finding to a Name of Allah', status: 'pending' },
    { id: 's4', domain: 'Names Extracted', action: 'Perform root fission on one Name extracted from your research', status: 'pending' },
    { id: 's5', domain: 'Taqdir Read', action: 'Document one measurement in your research as a taqdir', status: 'pending', taqdirLink: '"Every measurement given to a thing is prescribed by the owner of the highest embodiment of that concept."' },
  ];

  const yaqeenColors: Record<string, string> = { Ilm: '#8a8ab8', Ayn: '#2d8a87', Haqq: '#2d7a47' };
  const statusColors: Record<string, string> = { verified: '#2d7a47', submitted: '#D4A82A', draft: '#5a5a8a' };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl px-5 py-4" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
        <div className="flex items-center gap-3 mb-1">
          <span style={{ color, fontSize: 20 }}>◎</span>
          <div>
            <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: `${color}70` }}>Lineage Dashboard</p>
            <h3 className="font-mono text-lg font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Scientist</h3>
          </div>
          <div className="ml-auto">
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${color}60`, direction: 'rtl' }}>عَالِم</p>
          </div>
        </div>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>Research observation chains and Names extraction — seen first</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'observations', label: 'Observation Chain' },
          { id: 'names', label: 'Names Extracted' },
          { id: 'amanah', label: 'Amanah Tracker' },
          { id: 'garment', label: 'Garment View' },
        ].map(p => (
          <button key={p.id} onClick={() => setActivePanel(p.id as typeof activePanel)}
            className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
            style={{ background: activePanel === p.id ? `${color}12` : 'transparent', border: `1px solid ${activePanel === p.id ? `${color}35` : 'rgba(255,255,255,0.06)'}`, color: activePanel === p.id ? color : 'rgba(212,197,169,0.4)' }}>
            {p.label}
          </button>
        ))}
      </div>

      {activePanel === 'observations' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Observation Chain — communal tafsir of the Qadr</p>
          {observationChain.map(obs => (
            <div key={obs.id} className="rounded-lg p-3" style={{ background: `${color}05`, border: `1px solid ${color}15` }}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-mono text-[9px] font-black uppercase" style={{ color: '#d4c5a9' }}>{obs.encounter}</p>
                    <span className="font-mono text-[7px] px-1.5 py-0.5 rounded" style={{ background: `${yaqeenColors[obs.yaqeen]}12`, color: yaqeenColors[obs.yaqeen], border: `1px solid ${yaqeenColors[obs.yaqeen]}25` }}>{obs.yaqeen} Yaqeen</span>
                    <span className="font-mono text-[7px] px-1.5 py-0.5 rounded" style={{ background: `${statusColors[obs.status]}10`, color: statusColors[obs.status] }}>{obs.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {obs.names.map(n => (
                      <span key={n} className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: `${color}10`, color }}>{n}</span>
                    ))}
                  </div>
                </div>
                <span className="font-mono text-[7px]" style={{ color: 'rgba(212,197,169,0.3)', flexShrink: 0 }}>{obs.date}</span>
              </div>
            </div>
          ))}
          <div className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
            <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 10, lineHeight: 1.6, fontStyle: 'italic' }}>
              "You recognized what the Pen wrote. Your observation adds to the communal tafsir of the Qadr." — Every observation is a reading of what was already decreed.
            </p>
          </div>
        </div>
      )}

      {activePanel === 'names' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Names Extracted — from research to recognition</p>
          {[
            { name: 'Al-Latif', arabic: 'اللَّطِيف', encounters: 3, rootNote: 'Subtlety — the mycorrhizal network\'s invisible communication is Al-Latif made manifest' },
            { name: 'An-Nur', arabic: 'النُّور', encounters: 5, rootNote: 'Light — photosynthesis is An-Nur\'s provision converted into form by Al-Muhyi' },
            { name: 'Al-Khabir', arabic: 'الْخَبِير', encounters: 2, rootNote: 'All-Aware — the precision of ecological feedback loops is Al-Khabir\'s awareness in creation' },
          ].map(n => (
            <div key={n.name} className="rounded-lg p-3" style={{ background: `${color}05`, border: `1px solid ${color}15` }}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <p className="font-mono text-[10px] font-black" style={{ color }}>{n.name}</p>
                  <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: `${color}60`, direction: 'rtl' }}>{n.arabic}</p>
                </div>
                <span className="font-mono text-[8px]" style={{ color: `${color}60` }}>{n.encounters}× encountered</span>
              </div>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, lineHeight: 1.5, fontStyle: 'italic' }}>{n.rootNote}</p>
            </div>
          ))}
        </div>
      )}

      {activePanel === 'amanah' && <AmanahTracker role="scientist" color={color} items={amanahItems} />}
      {activePanel === 'garment' && <GarmentView role="scientist" color={color} />}
    </div>
  );
}

// ─── NATIVE DASHBOARD ─────────────────────────────────────────────────────────

function NativeDashboard() {
  const color = '#c9a84c';
  const [activePanel, setActivePanel] = useState<'stewardship' | 'planning' | 'amanah' | 'garment'>('stewardship');

  const stewardshipDomains = [
    { domain: 'Watershed Stewardship', name: 'Al-Latif', observation: 'Flow patterns, water quality, riparian health', status: 'active', color: '#2d8a87' },
    { domain: 'Forest Canopy', name: 'Al-Khaliq', observation: 'Species diversity, canopy coverage, succession stage', status: 'active', color: '#2d7a47' },
    { domain: 'Soil Memory', name: 'Al-Khabir', observation: 'Ancestral land use, soil composition, cultural memory', status: 'needs-attention', color: '#c9a84c' },
    { domain: 'Seasonal Ceremony', name: 'Al-Qayyum', observation: 'Seasonal rhythms honored through practice', status: 'active', color: '#8a8ab8' },
  ];

  const planningDomains = [
    { label: 'Sustainable Urban Planning', desc: 'Land use decisions as Amanah — what does the land\'s taqdir call for?', href: '/muslim-resource-guide', color: '#c9a84c' },
    { label: 'Ancestral Knowledge Systems', desc: 'Indigenous ecological knowledge as Ilm Yaqeen — verified by generations', href: '/keys-to-kulli-haal', color: '#2d7a47' },
    { label: 'Community Land Trust', desc: 'Decentralized stewardship — "nothing of it are you retainers" (Quran 15:23)', href: '/muslim-resource-guide', color: '#2d8a87' },
  ];

  const amanahItems: AmanahItem[] = [
    { id: 'n1', domain: 'Land Observation', action: 'Document one ecological observation from your ancestral territory', status: 'active', taqdirLink: 'The land\'s condition is a taqdir — a measure decreed to it by the conditions that have unfolded through human choices.' },
    { id: 'n2', domain: 'Ancestral Knowledge', action: 'Record one piece of ancestral ecological knowledge', status: 'pending', taqdirLink: 'Ancestral knowledge is Ilm Yaqeen — certainty through a chain of transmission across generations.' },
    { id: 'n3', domain: 'Stewardship Act', action: 'Perform one act of land stewardship and document it', status: 'pending' },
    { id: 'n4', domain: 'Planning Amanah', action: 'Attend one land use meeting and document what you heard', status: 'pending', taqdirLink: '"What value propositions are directing the native\'s ethical decisions regarding sustainable urban planning?" — Al-Qadir Section' },
  ];

  const statusColors: Record<string, string> = { active: '#2d7a47', 'needs-attention': '#D4A82A' };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl px-5 py-4" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
        <div className="flex items-center gap-3 mb-1">
          <span style={{ color, fontSize: 20 }}>◈</span>
          <div>
            <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: `${color}70` }}>Lineage Dashboard</p>
            <h3 className="font-mono text-lg font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Native</h3>
          </div>
          <div className="ml-auto">
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${color}60`, direction: 'rtl' }}>أَصِيل</p>
          </div>
        </div>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>Ecological stewardship and sustainable planning — seen first</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'stewardship', label: 'Stewardship' },
          { id: 'planning', label: 'Planning Domains' },
          { id: 'amanah', label: 'Amanah Tracker' },
          { id: 'garment', label: 'Garment View' },
        ].map(p => (
          <button key={p.id} onClick={() => setActivePanel(p.id as typeof activePanel)}
            className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
            style={{ background: activePanel === p.id ? `${color}12` : 'transparent', border: `1px solid ${activePanel === p.id ? `${color}35` : 'rgba(255,255,255,0.06)'}`, color: activePanel === p.id ? color : 'rgba(212,197,169,0.4)' }}>
            {p.label}
          </button>
        ))}
      </div>

      {activePanel === 'stewardship' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Ecological Stewardship — land domains under care</p>
          {stewardshipDomains.map(d => (
            <div key={d.domain} className="rounded-lg p-4" style={{ background: `${d.color}05`, border: `1px solid ${d.color}15` }}>
              <div className="flex items-start justify-between mb-1">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-[9px] font-black uppercase" style={{ color: '#d4c5a9' }}>{d.domain}</p>
                    <span className="font-mono text-[8px] px-1.5 py-0.5 rounded" style={{ background: `${d.color}10`, color: d.color }}>{d.name}</span>
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, marginTop: 2 }}>{d.observation}</p>
                </div>
                <span className="font-mono text-[7px] uppercase px-2 py-0.5 rounded flex-shrink-0" style={{ background: `${statusColors[d.status]}10`, color: statusColors[d.status], border: `1px solid ${statusColors[d.status]}20` }}>{d.status}</span>
              </div>
            </div>
          ))}
          <div className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
            <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, lineHeight: 1.6, fontStyle: 'italic' }}>
              "What value propositions are directing the native's ethical decisions regarding sustainable urban planning in their locality?" — Al-Qadir Section
            </p>
          </div>
        </div>
      )}

      {activePanel === 'planning' && (
        <div className="space-y-3">
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Sustainable Planning Domains</p>
          <div className="grid grid-cols-1 gap-3">
            {planningDomains.map(p => (
              <Link key={p.label} href={p.href}>
                <div className="rounded-xl p-4 cursor-pointer transition-all" style={{ background: `${p.color}06`, border: `1px solid ${p.color}18` }}>
                  <p className="font-mono text-[9px] font-black uppercase mb-1" style={{ color: p.color }}>{p.label}</p>
                  <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 10 }}>{p.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {activePanel === 'amanah' && <AmanahTracker role="native" color={color} items={amanahItems} />}
      {activePanel === 'garment' && <GarmentView role="native" color={color} />}
    </div>
  );
}

// ─── LINEAGE DASHBOARD SELECTOR ───────────────────────────────────────────────

const LINEAGE_META: Record<LineageRole, { label: string; icon: string; color: string; arabic: string; tagline: string }> = {
  engineer: { label: 'Engineer', icon: '⚙', color: '#2d8a87', arabic: 'مُهَنْدِس', tagline: 'Systems & provision first' },
  farmer: { label: 'Farmer', icon: '⬡', color: '#D4A82A', arabic: 'فَلَّاح', tagline: 'Soil & seasonal cycles first' },
  'returning-citizen': { label: 'Returning Citizen', icon: '⌘', color: '#5a5a8a', arabic: 'عَائِد', tagline: 'Mizan & restoration first' },
  teacher: { label: 'Teacher', icon: '✦', color: '#8a8ab8', arabic: 'مُعَلِّم', tagline: 'Curriculum & knowledge first' },
  scientist: { label: 'Scientist', icon: '◎', color: '#2d7a47', arabic: 'عَالِم', tagline: 'Research & observation first' },
  native: { label: 'Native', icon: '◈', color: '#c9a84c', arabic: 'أَصِيل', tagline: 'Stewardship & land first' },
};

export function LineageDashboards() {
  const [activeRole, setActiveRole] = useState<LineageRole | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('haal_caretaker_role') as LineageRole | null;
    if (saved && LINEAGE_META[saved]) setActiveRole(saved);
  }, []);

  const selectRole = (role: LineageRole) => {
    setActiveRole(role);
    if (typeof window !== 'undefined') localStorage.setItem('haal_caretaker_role', role);
  };

  return (
    <div>
      {/* Role selector grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {(Object.entries(LINEAGE_META) as [LineageRole, typeof LINEAGE_META[LineageRole]][]).map(([role, meta]) => (
          <button
            key={role}
            onClick={() => selectRole(role)}
            className="rounded-xl p-4 text-left transition-all"
            style={{
              background: activeRole === role ? `${meta.color}12` : `${meta.color}05`,
              border: `1px solid ${activeRole === role ? `${meta.color}40` : `${meta.color}18`}`,
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span style={{ color: meta.color, fontSize: 16 }}>{meta.icon}</span>
              <p className="font-mono text-[9px] font-black uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</p>
            </div>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: `${meta.color}50`, direction: 'rtl', marginBottom: 2 }}>{meta.arabic}</p>
            <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10 }}>{meta.tagline}</p>
            {activeRole === role && (
              <div className="mt-2 flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                <span className="font-mono text-[7px] uppercase" style={{ color: meta.color }}>Active</span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Active dashboard */}
      {activeRole === 'engineer' && <EngineerDashboard />}
      {activeRole === 'farmer' && <FarmerDashboard />}
      {activeRole === 'returning-citizen' && <ReturningCitizenDashboard />}
      {activeRole === 'teacher' && <TeacherDashboard />}
      {activeRole === 'scientist' && <ScientistDashboard />}
      {activeRole === 'native' && <NativeDashboard />}

      {!activeRole && (
        <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>Select Your Lineage</p>
          <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 12 }}>Choose a role above to see your customized dashboard — each role sees its most relevant data first</p>
        </div>
      )}
    </div>
  );
}

export default LineageDashboards;
